/**
 * Ticket Lifecycle v2 — Slice 4A.2
 *
 * Subscribes to Postgres realtime changes on `ticket_custody` for the
 * active shop. On any INSERT/UPDATE/DELETE, invalidates the React Query
 * keys that the slice-3 / slice-4A surfaces depend on:
 *
 *   - `['ticket-custody', <ticket_id>]`    (TicketCard chip, kebab item)
 *   - `['ticket-custody-events', <id>]`    (future history panels)
 *   - `['foh-custody-queue', <shop_id>]`   (slice-4A queue page)
 *
 * Mirror of `useConfirmationAlerts.ts` and `useLiveReshuffle.ts`
 * realtime subscription style.
 *
 * Boundary: this hook does NOT touch scheduling, timer, parking,
 * rollover, bay assignment, or `tickets.status` invalidation. The
 * Chronomancer-bound queries are unaffected.
 */
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { logRealtimeStatus } from '@/utils/realtimeStatusLog';

export function useTicketCustodyRealtime(
  shopId: string | null | undefined,
  enabled: boolean,
) {
  const qc = useQueryClient();

  useEffect(() => {
    if (!enabled || !shopId) return;

    const channel = supabase
      // Per-shop channel name so two browser tabs from the same user
      // don't collide. Matches the pattern at
      // `useConfirmationAlerts.ts:23`.
      .channel(`ticket-custody-${shopId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'ticket_custody',
          filter: `shop_id=eq.${shopId}`,
        },
        (payload) => {
          // Defence-in-depth shop guard. The server-side filter already
          // scopes by shop_id; mirror the pattern from
          // `useConfirmationAlerts.ts:40` so a misconfigured filter never
          // surfaces another shop's invalidations.
          const newShop = (payload.new as { shop_id?: string } | null)?.shop_id;
          const oldShop = (payload.old as { shop_id?: string } | null)?.shop_id;
          if (newShop && newShop !== shopId) return;
          if (oldShop && oldShop !== shopId) return;

          // Pull the ticket_id from either side of the payload.
          const ticketId =
            (payload.new as { ticket_id?: string } | null)?.ticket_id ??
            (payload.old as { ticket_id?: string } | null)?.ticket_id ??
            null;

          if (ticketId) {
            qc.invalidateQueries({ queryKey: ['ticket-custody', ticketId] });
            qc.invalidateQueries({
              queryKey: ['ticket-custody-events', ticketId],
            });
            qc.invalidateQueries({
              queryKey: ['ticket-custody-history', ticketId],
            });
          }
          // The queue read always refetches: even if we didn't get a
          // ticket_id (extremely unlikely), the queue depends on the
          // shop's full set, so we refresh.
          qc.invalidateQueries({ queryKey: ['foh-custody-queue', shopId] });
        },
      )
      .subscribe((status, err) => {
        // Batch 11: normalized via `logRealtimeStatus`. CLOSED-with-
        // no-err is the intentional supabase-js cleanup signal and
        // logs at debug; CHANNEL_ERROR / TIMED_OUT / CLOSED-with-err
        // still surface as warn/error so a silent TIMED_OUT that
        // freezes the custody queue is diagnosable.
        logRealtimeStatus('[useTicketCustodyRealtime]', status, err);
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [shopId, enabled, qc]);
}
